/**
 * Geschwindigkeit mit Rampe (S6).
 *
 * Sascha: „Langsamer werden statt umschalten, und die Tonhöhe bleibt, wo sie
 * war.“ Bis hierher hatte ein Clip *eine* Zahl fürs Tempo; ein Wechsel war
 * ein Schnitt. Eine Rampe läuft über die Länge des Clips von einem Tempo zum
 * anderen.
 *
 * Gerechnet wird in der **Zeit des Clips** (der fertigen, sichtbaren Länge D).
 * Das Tempo darin ist geradlinig:
 *
 *     s(τ) = von + (bis − von) · τ/D        für τ in [0, D]
 *
 * „Tempo 2“ heisst: in einer Sekunde Film steckt Material von zwei Sekunden.
 * Wie viel Material der ganze Clip verbraucht, ist damit die Fläche unter
 * s -- bei einer Geraden einfach der Mittelwert mal die Länge. Das ist der
 * Grund, warum die Rampe geradlinig ist und keine Kurve: man kann sie im Kopf
 * ausrechnen, und der Schnitt dahinter rutscht nicht.
 */
/**
 * Die Rampe eines Clips. Ohne `speedBis` ist es eine waagerechte: Anfang und
 * Ende gleich, und dann rechnet alles hier dasselbe wie vorher.
 */
export function rampeVon(clip) {
    return { von: clip.speed, bis: clip.speedBis ?? clip.speed };
}
/** Hat der Clip überhaupt eine Rampe? */
export function hatRampe(clip) {
    return clip.speedBis !== null && clip.speedBis !== undefined && Math.abs(clip.speedBis - clip.speed) > 1e-9;
}
/**
 * Wie viel Quellmaterial ein Clip mit dieser Rampe verbraucht.
 *
 * Die Fläche unter einer Geraden: Mittelwert mal Länge. Ohne Rampe ist das
 * dasselbe wie `dauer * tempo`, und genau so muss es sein -- sonst rutschte
 * jeder Schnitt, sobald jemand eine Rampe auf „überall gleich“ stellt.
 */
export function quellLaenge(dauer, r) {
    return dauer * ((r.von + r.bis) / 2);
}
/**
 * Wo im Quellmaterial man ist, wenn im Clip τ Sekunden vergangen sind.
 *
 * Das Integral über s, also bei einer Geraden die Fläche eines Trapezes.
 */
export function quellZeit(tau, dauer, r) {
    const t = Math.max(0, Math.min(dauer, tau));
    return r.von * t + ((r.bis - r.von) * t * t) / (2 * dauer);
}
/**
 * Die Umkehrung: wie weit der Clip ist, wenn im Material t Sekunden gelesen
 * sind. Das braucht `setpts`, denn ffmpeg rechnet auf den Zeitstempeln der
 * **Quelle** und muss daraus die des Films machen.
 *
 * Aus `von·τ + (bis−von)·τ²/(2D) = t` wird eine quadratische Gleichung; von
 * ihren beiden Lösungen ist die mit dem Plus die, die bei τ = 0 anfängt.
 */
export function clipZeit(t, dauer, r) {
    const d = r.bis - r.von;
    if (Math.abs(d) < 1e-9)
        return t / r.von;
    const wurzel = Math.sqrt(r.von * r.von + (2 * d * t) / dauer);
    return ((wurzel - r.von) * dauer) / d;
}
/**
 * Der `setpts`-Ausdruck für eine Rampe.
 *
 * `T` ist in ffmpeg die Zeit des Eingangs in Sekunden. Herauskommen muss die
 * Zeit im Film, in Zeitbasis-Einheiten -- darum `/TB`.
 *
 * Ohne Rampe bleibt es bei der einfachen Teilung: ein Ausdruck, der dasselbe
 * tut, wäre nur schwerer zu lesen und langsamer.
 */
export function setptsAusdruck(dauer, r) {
    const d = r.bis - r.von;
    if (Math.abs(d) < 1e-9)
        return `PTS/${zahl(r.von)}`;
    // (sqrt(von² + 2·(bis−von)·T/D) − von) · D/(bis−von)
    return `((sqrt(${zahl(r.von * r.von)}+${zahl((2 * d) / dauer)}*T)-${zahl(r.von)})*${zahl(dauer / d)})/TB`;
}
/**
 * Der Ton in Stufen.
 *
 * `atempo` nimmt eine feste Zahl und hält dabei die Tonhöhe -- genau das, was
 * Sascha wollte. Eine *veränderliche* Zahl kennt es nicht. Also wird der Ton
 * in gleich lange Stücke des Films geschnitten, jedes mit dem Tempo, das in
 * seiner Mitte gilt, und danach wieder aneinandergehängt.
 *
 * Wie fein, entscheidet das Ohr: bei Stufen von einem Zehntel einer Sekunde
 * liegt der Sprung zwischen zwei Stufen bei einer Rampe von 1 auf 2 über vier
 * Sekunden bei 2,5 % -- ein Viertel eines Halbtons in der *Geschwindigkeit*,
 * und die Tonhöhe ändert sich dabei gar nicht. Zu feine Stufen kosten dagegen
 * Rechenzeit und bringen nichts: `atempo` selbst arbeitet mit Fenstern in
 * dieser Grössenordnung.
 */
export const STUFE_SEK = 0.1;
export function tonStufen(dauer, r, stufe = STUFE_SEK) {
    if (Math.abs(r.bis - r.von) < 1e-9)
        return [{ von: 0, bis: dauer, tempo: r.von }];
    const n = Math.max(1, Math.ceil(dauer / stufe));
    const stufen = [];
    for (let i = 0; i < n; i++) {
        const von = (dauer * i) / n;
        const bis = (dauer * (i + 1)) / n;
        // Das Tempo in der Mitte der Stufe: so verbraucht die Stufe genau so viel
        // Material, wie die Rampe an dieser Stelle vorsieht -- bei einer Geraden
        // ist der Mittelwert der Wert in der Mitte.
        const tempo = r.von + ((r.bis - r.von) * ((von + bis) / 2)) / dauer;
        stufen.push({ von, bis, tempo });
    }
    return stufen;
}
function zahl(n) {
    return Number.isInteger(n) ? String(n) : n.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
}
//# sourceMappingURL=rampe.js.map